import { defineStore } from "pinia";
import type {
  ContentType,
  PageContent,
  PageContentInitialState,
  Tag,
  Topic
} from "@/types";

export const usePageContentStore = defineStore("pageContent", {
  persist: true,
  state: (): PageContentInitialState => ({
    currentPageContent: {} as PageContent,
    sectionContent: [] as PageContent[],
    lastUpdated: 0,
    tags: [] as Tag[],
    contentTypes: [] as ContentType[],
    topics: [] as Topic[]
  }),

  getters: {
    getLastUpdated: state => state.lastUpdated,
    getCurrentPage: state => state.currentPageContent,
    getSideBlocks: state => state.currentPageContent?.sideblocks || [],
    getSideBlocksByRegion: state => (region: string) => {
      return (state.currentPageContent?.sideblocks || []).filter(
        block => block.region === region
      );
    },
    getTags: state => {
      return state.tags.filter(tag => tag.id).sort((a, b) => a.id - b.id);
    },
    getTagCloud: state => {
      const tagsLarge = 35;
      const tagsSmall = 12;
      const items = state.tags.filter(
        tag => tag && typeof tag.filesCount === "number"
      );

      if (items.length === 0) return [];

      const tagsSorted = [...items].sort((a, b) => a.filesCount - b.filesCount);
      const max = tagsSorted[tagsSorted.length - 1].filesCount;
      const min = tagsSorted[0].filesCount;
      const spread = max - min || 1;
      const fontSpread = tagsLarge - tagsSmall || 1;
      const fontStep = fontSpread / spread;

      return items
        .map(item => ({
          ...item,
          fontSize: tagsSmall + (item.filesCount - min) * fontStep
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    getContentTypes: state => state.contentTypes,
    getTopics: state => state.topics,
    getSectionContent: state => state.sectionContent,
    getFooter: state =>
      state.sectionContent.find(page => page.slug === "footer") || {},
    getLoginVideos: state =>
      state.sectionContent.find(page => page.slug === "login-videos") || {}
  },

  actions: {
    setLastUpdated(data: number) {
      this.lastUpdated = data;
    },
    setCurrentPage(data: { pageContent: PageContent }) {
      this.currentPageContent = data.pageContent;
    },
    setSectionContent(data: { sectionContent: PageContent[] }) {
      this.sectionContent = data.sectionContent;
    },
    setTags(data: Tag[]) {
      this.tags = data;
    },
    setContentTypes(data: ContentType[]) {
      this.contentTypes = data;
    },
    setTopics(data: Topic[]) {
      this.topics = data;
    }
  }
});
